/**
 * Local presentation models for the Care Plan tab (MW-11141).
 *
 * This remains separate from the AIM wire contract. The store adapts live
 * Case resources into these presentation records while dev fixtures use the
 * same shape.
 */

/** NEW — local presentation; not a wire contract. */
export type CarePlanCaseStatus = string;

/** Display labels — from design CASE_LABEL, trusted as backend-aligned vocabulary (D1). */
export const CARE_PLAN_CASE_STATUS_LABEL: Record<CarePlanCaseStatus, string> = {
  planned: 'Planned',
  in_progress: 'In progress',
  monitor: 'Monitor',
  closed: 'Closed',
  canceled: 'Canceled',
  suggested: 'Suggested',
};

/** Statuses in scope for creation and the default active case list (excludes closed + suggested). */
export const ACTIVE_CASE_STATUSES: readonly CarePlanCaseStatus[] = [
  'planned',
  'in_progress',
  'monitor',
];

/** Fixed group-by-Status order (PRD §4 — not alphabetical, not declaration order). */
export const CASE_STATUS_GROUP_ORDER: readonly CarePlanCaseStatus[] = [
  'in_progress',
  'monitor',
  'planned',
  'closed',
  'canceled',
  'suggested',
];

/** NEW — close reasons (design CLOSED_REASONS / PRD). Ignore design fixture value `no_longer_relevant` (stale). */
export type CarePlanClosedReason =
  | 'achieved' // display: Complete
  | 'abandoned' // Abandoned
  | 'no_longer_eligible' // No longer eligible
  | 'cancelled'; // Cancelled

export const CARE_PLAN_CLOSED_REASON_LABEL: Record<
  CarePlanClosedReason,
  string
> = {
  achieved: 'Complete',
  abandoned: 'Abandoned',
  no_longer_eligible: 'No longer eligible',
  cancelled: 'Cancelled',
};

/** NEW — goal status for the Manage goals modal. */
export type CarePlanGoalStatus =
  | 'active'
  | 'achieved'
  | 'abandoned'
  | 'revised';

/** Max active goals per client (PRD §4 goal cap enforcement). */
export const CARE_PLAN_MAX_ACTIVE_GOALS = 3;

/** NEW — local / provisional. */
export interface CarePlanCase {
  id: string;
  number: string; // display e.g. CASE-0142 — format TBD backend
  title: string; // = objective type name
  /** AIM `openedAt`; displayed as the case creation date. */
  createdAt?: string;
  description: string; // required FA free text, 1-250 chars (D19)
  domain: string; // taxonomy domain key, e.g. 'meet_basic_needs'
  objective: string; // taxonomy objective label
  status: CarePlanCaseStatus;
  goalIds: string[]; // all linked goals, including historical associations
  closedReason?: CarePlanClosedReason;
  /** Raw AIM closure code, retained so unknown future values still render safely. */
  closedReasonCode?: string;
  closedNote?: string; // max 1000
  closedAt?: string;
  priorStatus?: CarePlanCaseStatus; // for reopen (D17)
  /** AIM identifiers retained while reference-data labels are unavailable. */
  domainId?: number;
  objectiveTypeId?: number;
  checkInCadenceDays?: number | null;
}

/** NEW — local / provisional. */
export interface CarePlanGoal {
  id: string;
  displayText: string; // editable; max 200
  originalDescription: string | null; // set on create; never overwritten on edit
  status: CarePlanGoalStatus;
  sortOrder: number; // Goals bar + group-by-goal
  linkedCaseIds: string[];
}

export const CARE_PLAN_UNLINKED_GROUP_KEY = 'unlinked';
export const CARE_PLAN_UNLINKED_GROUP_LABEL = 'Not tied to a client goal';

/** Closed lifecycle states retain distinct API labels and can both be reopened. */
export function isClosedCaseStatus(status: string | null | undefined): boolean {
  return (
    status?.toLowerCase() === 'closed' || status?.toLowerCase() === 'canceled'
  );
}
